import {
  PatientReminderData,
  ArticleViewData,
  AggregatedArticleData,
} from "../../../types/reminderTypes";

// Helper function for safe string concatenation
const safeStringConcat = (
  str1: string | null | undefined,
  str2: string | null | undefined,
  separator: string = " "
): string => {
  const val1 = str1 || "";
  const val2 = str2 || "";
  return `${val1}${val1 && val2 ? separator : ""}${val2}`.trim();
};

// Helper function for safe number conversion
const safeNumber = (value: any): number => {
  const num = Number(value);
  return isNaN(num) ? 0 : num;
};

export const transformPatientsData = (
  rawData: any[]
): PatientReminderData[] => {
  if (!Array.isArray(rawData)) {
    console.warn("transformPatientsData: Expected array, got:", typeof rawData);
    return [];
  }

  return rawData.map((user: any) => ({
    // Primary fields
    id: user?.id || 0,
    status: user?.status || "",
    note: user?.note || "/",
    therapyTaken: Boolean(user?.therapyTaken),
    willBeReadyFrom: user?.willBeReadyFrom || "",
    willBeReadyTo: user?.willBeReadyTo || "",
    created: user?.created || "", // Added - critical backend field
    modified: user?.modified || null, // Added - critical backend field

    // User fields (raw from API)
    userFirstName: user?.userFirstName || "",
    userSecondName: user?.userSecondName || "",
    userLBO: user?.userLBO || "",
    userPhone: user?.userPhone || "", // Keep raw userPhone
    userEmail: user?.userEmail || "",
    userResponse: user?.userResponse || null,

    // Computed fields (for display compatibility)
    name: safeStringConcat(user?.userFirstName, user?.userSecondName),
    phone: user?.userPhone || user?.userResponse?.phoneNumber || "",
    lbo: user?.userLBO || "",
    email: user?.userResponse
      ? user?.userResponse.username
      : user?.userEmail || "",

    // Related data
    items: Array.isArray(user?.items) ? user.items : [],
    groups: Array.isArray(user?.groups) ? user.groups : [],
    rfzoTherapyReminderGroupsIds: Array.isArray(
      user?.rfzoTherapyReminderGroupsIds
    )
      ? user.rfzoTherapyReminderGroupsIds
      : [],
  }));
};

export const transformToArticleView = (rawData: any[]): ArticleViewData[] => {
  if (!Array.isArray(rawData)) {
    console.warn(
      "transformToArticleView: Expected array, got:",
      typeof rawData
    );
    return [];
  }

  const articleViewData: ArticleViewData[] = [];

  rawData.forEach((patient) => {
    if (!patient || !Array.isArray(patient.items)) {
      return;
    }

    patient.items.forEach((item: any, index: number) => {
      const articleId = item?.itemResponse?.id || item?.itemId || 0;
      const patientId = patient?.id || 0;

      articleViewData.push({
        id: `${patientId}-${articleId}-${index}`, // More unique ID
        articleName: item?.itemResponse?.name || item?.articleName || "N/A",
        articleId,
        patientName: safeStringConcat(
          patient?.userFirstName,
          patient?.userSecondName
        ),
        patientLBO: patient?.userLBO || "",
        patientId,
        quantity: safeNumber(item?.rfzoQuantity) || 1,
        dueDate: patient?.willBeReadyTo || "",
        willBeReadyFrom: patient?.willBeReadyFrom || "",
        willBeReadyTo: patient?.willBeReadyTo || "",
        reminderStatus: patient?.status || "",
        therapyTaken: Boolean(patient?.therapyTaken),
        note: patient?.note || "",
        patientPhone:
          patient?.userPhone || patient?.userResponse?.phoneNumber || "",
        patientEmail:
          patient?.userResponse?.username || patient?.userEmail || "",
        rfzoPrice: item?.itemResponse?.rfzoPrice,
      });
    });
  });

  return articleViewData;
};

export const transformAggregatedData = (
  rawData: any[]
): AggregatedArticleData[] => {
  if (!Array.isArray(rawData)) {
    console.warn(
      "transformAggregatedData: Expected array, got:",
      typeof rawData
    );
    return [];
  }

  return rawData.map((article: any) => ({
    articleName: article?.itemResponse?.name || "N/A",
    articleId: article?.itemResponse?.id || 0,
    totalQuantity: safeNumber(article?.rfzotherapiesQuantity),
    availableQuantity: safeNumber(article?.stockQuantity),
    reminderCount: safeNumber(article?.reminderCount) || 1,
  }));
};

export const aggregateArticlesFromPatients = (
  patientData: PatientReminderData[]
): AggregatedArticleData[] => {
  if (!Array.isArray(patientData)) {
    return [];
  }

  const articleMap = new Map<number, AggregatedArticleData>();

  patientData.forEach((patient) => {
    if (!Array.isArray(patient.items)) {
      return;
    }

    patient.items.forEach((item) => {
      const articleId = item?.itemResponse?.id || item?.itemId || 0;
      const articleName =
        item?.itemResponse?.name || item?.articleName || "N/A";
      const quantity = safeNumber(item?.rfzoQuantity) || 1;

      if (articleMap.has(articleId)) {
        const existing = articleMap.get(articleId)!;
        existing.totalQuantity += quantity;
        existing.reminderCount += 1;
      } else {
        articleMap.set(articleId, {
          articleName,
          articleId,
          totalQuantity: quantity,
          availableQuantity: 0, // Will need separate API call for stock
          reminderCount: 1,
        });
      }
    });
  });

  return Array.from(articleMap.values());
};
